import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonHeader,
  IonPage,
  IonRow,
  IonTitle,
  IonToolbar,
  useIonRouter,
} from '@ionic/react'
import { Redirect } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ErrorMessage } from '@hookform/error-message'

import { useToast } from '../../contexts/toastContext'
import { useCreateClient } from '../../hooks/clients/mutations'
import { StyledError } from '../../components/ui/form/errors'
import { TextInput } from '../../components/ui/form/input'
import {
  useClientInfo,
  useClientInfoActions,
} from '../../clients/client-info-store'
import { addClientSchema } from '../../schema/add-client.schema'

import type { AddClientSchema } from '../../schema/add-client.schema'

type FormData = Pick<AddClientSchema, 'firstName' | 'lastName' | 'phoneNumber'>

function CashierAddCustomerPage() {
  const setToast = useToast()

  const { phoneNumber } = useClientInfo()
  const { resetClientInfo } = useClientInfoActions()

  const router = useIonRouter()

  const createClient = useCreateClient()

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<FormData>({
    defaultValues: {
      phoneNumber: phoneNumber ?? '',
      firstName: '',
      lastName: '',
    },
    resolver: zodResolver(addClientSchema.partial()),
  })

  function handleFinish(formData: FormData) {
    createClient.mutate(formData, {
      onSuccess: (data) => {
        resetClientInfo()
        router.push(
          `/rotate/cashier/loyalty-info/${data.result.client.objectId}`
        )
      },
      onError: (error) => {
        setToast(
          `Something went wrong:  ${
            error instanceof Error ? error.message : 'Unknown Error'
          }`
        )
      },
    })
  }

  if (!phoneNumber) return <Redirect exact to="/rotate/cashier/check-in" />

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Add Customer</IonTitle>
          <IonButtons slot="start">
            <IonButton
              color="secondary"
              fill="outline"
              onClick={() => {
                resetClientInfo()
                if (router.canGoBack()) router.goBack()
                router.push('/rotate/cashier/check-in', 'back')
              }}
            >
              Cancel
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding-horizontal">
        <div className="h-full">
          <div className="mx-auto flex size-full max-w-xl flex-col items-center justify-center">
            <h1 className="text-5xl">Add Customer</h1>
            <div className="py-3" />
            <p className="text-3xl">
              That customer is not in our records. Please add them here.
            </p>
            <div className="py-6" />
            <form onSubmit={handleSubmit(handleFinish)} className="w-full">
              <input type="hidden" {...register('phoneNumber')} />
              <ErrorMessage
                as={StyledError}
                name="phoneNumber"
                errors={errors}
              />
              <IonRow className="w-full">
                <IonCol size="6">
                  <TextInput
                    disabled={createClient.isLoading}
                    control={control}
                    name="firstName"
                    placeholder="First Name"
                  />
                </IonCol>
                <IonCol size="6">
                  <TextInput
                    disabled={createClient.isLoading}
                    control={control}
                    name="lastName"
                    placeholder="Last Name"
                  />
                </IonCol>
              </IonRow>
              <ErrorMessage as={StyledError} name="firstName" errors={errors} />
              <ErrorMessage as={StyledError} name="lastName" errors={errors} />

              <div className="py-10" />
              <div className="flex justify-center">
                <button
                  disabled={createClient.isLoading}
                  type="submit"
                  color="secondary"
                  className="bg-ion-color-secondary text-ion-color-primary rounded-full px-20 py-3 text-3xl font-semibold"
                >
                  Continue
                </button>
              </div>
            </form>
          </div>
        </div>
      </IonContent>
    </IonPage>
  )
}

export { CashierAddCustomerPage }
